<template>
    <div>
        <h2 class="font-header text-[3rem] font-bold text-primary mb-6">Careers Management</h2>

        <AdminPageDescription page-slug="careers" page-display-name="Careers" />

        <div v-if="showForm" class="bg-white p-6 rounded-lg shadow-md mb-6">
            <h3 class="font-header text-[1.5rem] font-bold mb-4">{{ editingCareer ? 'Edit' : 'Add' }} Career</h3>
            <form @submit.prevent="saveCareer" class="space-y-4">
                <div class="grid grid-cols-2 gap-4">
                    <div>
                        <label class="block font-body text-[1.125rem] leading-[1.75] mb-1">Title</label>
                        <input v-model="formData.title" type="text" required
                            class="w-full px-3 py-2 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-body text-[1.125rem] leading-[1.75]" />
                    </div>
                    <div>
                        <label class="block font-body text-[1.125rem] leading-[1.75] mb-1">Department</label>
                        <input v-model="formData.department" type="text" required
                            class="w-full px-3 py-2 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-body text-[1.125rem] leading-[1.75]" />
                    </div>
                    <div>
                        <label class="block font-body text-[1.125rem] leading-[1.75] mb-1">Location</label>
                        <input v-model="formData.location" type="text"
                            class="w-full px-3 py-2 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-body text-[1.125rem] leading-[1.75]" />
                    </div>
                    <div>
                        <label class="block font-body text-[1.125rem] leading-[1.75] mb-1">Employment Type</label>
                        <select v-model="formData.employment_type" required
                            class="w-full px-3 py-2 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-body text-[1.125rem] leading-[1.75]">
                            <option value="full-time">Full-time</option>
                            <option value="part-time">Part-time</option>
                            <option value="contract">Contract</option>
                            <option value="internship">Internship</option>
                        </select>
                    </div>
                    <div class="col-span-2">
                        <label class="block font-body text-[1.125rem] leading-[1.75] mb-1">Description</label>
                        <textarea v-model="formData.description" rows="6" required
                            class="w-full px-3 py-2 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-body text-[1.125rem] leading-[1.75]"></textarea>
                    </div>
                    <div class="col-span-2">
                        <div class="flex justify-between items-center mb-2">
                            <label class="block font-body text-[1.125rem] leading-[1.75]">Job Description
                                Subsections</label>
                            <button type="button" @click="addSubsection"
                                class="px-3 py-1 bg-green-700 text-white text-sm rounded-md hover:bg-green-800 transition-colors font-body text-[1.125rem] leading-[1.75]">
                                Add Subsection
                            </button>
                        </div>
                        <div v-if="formData.subsections.length === 0"
                            class="font-body text-[1.125rem] leading-[1.75] text-gray-500 mb-2">
                            No subsections added. Click "Add Subsection" to create custom sections.
                        </div>
                        <div v-for="(subsection, index) in formData.subsections" :key="index"
                            class="border border-gray-300 rounded-md p-4 mb-3 bg-gray-50">
                            <div class="flex justify-between items-start mb-2">
                                <span class="font-body text-[1.125rem] leading-[1.75] text-gray-700">Subsection {{ index
                                    + 1 }}</span>
                                <button type="button" @click="removeSubsection(index)"
                                    class="text-red-600 hover:text-red-700 font-body text-[1.125rem] leading-[1.75]">Remove</button>
                            </div>
                            <div class="space-y-2">
                                <div>
                                    <label
                                        class="block text-xs font-body leading-[1.75] mb-1 text-gray-600">Title</label>
                                    <input v-model="subsection.title" type="text" required
                                        class="w-full px-3 py-2 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-body text-[1.125rem] leading-[1.75] text-sm"
                                        placeholder="e.g., Required Skills, Compensation, Benefits" />
                                </div>
                                <div>
                                    <label
                                        class="block text-xs font-body leading-[1.75] mb-1 text-gray-600">Content</label>
                                    <textarea v-model="subsection.content" rows="4" required
                                        class="w-full px-3 py-2 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-body text-[1.125rem] leading-[1.75] text-sm"
                                        placeholder="Enter the content for this subsection..."></textarea>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="flex gap-2">
                    <button type="submit"
                        class="px-6 py-2 bg-green-700 text-white rounded-md hover:bg-green-800 transition-colors font-body text-[1.125rem] leading-[1.75]">
                        {{ editingCareer ? 'Update' : 'Create' }}
                    </button>
                    <button type="button" @click="cancelForm"
                        class="px-6 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors font-body text-[1.125rem] leading-[1.75]">
                        Cancel
                    </button>
                </div>
            </form>
        </div>

        <div class="mb-4 flex justify-end">
            <button @click="showForm = true; editingCareer = null"
                class="px-6 py-2 bg-green-700 text-white rounded-md hover:bg-green-800 transition-colors font-body text-[1.125rem] leading-[1.75]">
                Add New Career
            </button>
        </div>

        <div class="bg-white rounded-lg shadow-md overflow-hidden">
            <div class="w-full overflow-x-auto">
                <table class="w-full min-w-max">
                    <thead class="bg-green-700 text-white">
                        <tr>
                            <th class="px-4 py-3 text-left font-header text-[1.125rem] font-bold">Title</th>
                            <th class="px-4 py-3 text-left font-header text-[1.125rem] font-bold">Department</th>
                            <th class="px-4 py-3 text-left font-header text-[1.125rem] font-bold">Type</th>
                            <th class="px-4 py-3 text-left font-header text-[1.125rem] font-bold">Status</th>
                            <th class="px-4 py-3 text-left font-header text-[1.125rem] font-bold">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="career in careers" :key="career.id"
                            class="border-b border-creme-200 hover:bg-creme-50">
                            <td class="px-4 py-3 font-body text-body leading-[1.75] text-gray-900">{{ career.title }}
                            </td>
                            <td class="px-4 py-3 font-body text-body leading-[1.75] text-gray-900">{{ career.department
                            }}</td>
                            <td class="px-4 py-3 font-body text-body leading-[1.75] capitalize text-gray-900">{{
                                career.employment_type }}</td>
                            <td class="px-4 py-3 font-body text-body leading-[1.75]">
                                <span :class="career.is_active ? 'text-green-600' : 'text-red-600'">
                                    {{ career.is_active ? 'Active' : 'Inactive' }}
                                </span>
                            </td>
                            <td class="px-4 py-3">
                                <button @click="editCareer(career)"
                                    class="text-green-700 hover:text-green-800 mr-3 font-header text-[1.125rem] font-bold">Edit</button>
                                <button @click="toggleStatus(career)"
                                    :class="career.is_active ? 'text-yellow-600 hover:text-yellow-700 font-header text-[1.125rem] font-bold' : 'text-green-600 hover:text-green-700 font-header text-[1.125rem] font-bold'"
                                    class="mr-3">
                                    {{ career.is_active ? 'Deactivate' : 'Activate' }}
                                </button>
                                <button @click="deleteCareer(career.id)"
                                    class="text-red-600 hover:text-red-700 font-header text-[1.125rem] font-bold">Delete</button>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <div v-if="loading" class="p-4 text-center">
                <Loader size="medium" />
            </div>
            <div v-else-if="careers.length === 0"
                class="p-8 text-center text-gray-500 font-body text-body leading-[1.75]">
                No careers found
            </div>
            <div v-if="pagination && pagination.total_pages > 1"
                class="p-4 flex justify-between items-center border-t border-creme-200">
                <button @click="loadCareers(currentPage - 1)" :disabled="!pagination.prev_page"
                    :class="['px-4 py-2 rounded-md font-header text-[1.125rem] font-bold', pagination.prev_page ? 'bg-green-700 text-white hover:bg-green-800' : 'bg-gray-300 text-gray-500 cursor-not-allowed']">
                    Previous
                </button>
                <span class="font-header text-[1.125rem] font-bold text-gray-700">
                    Page {{ pagination.page }} of {{ pagination.total_pages }} ({{ pagination.total }} total)
                </span>
                <button @click="loadCareers(currentPage + 1)" :disabled="!pagination.next_page"
                    :class="['px-4 py-2 rounded-md font-header text-[1.125rem] font-bold', pagination.next_page ? 'bg-green-700 text-white hover:bg-green-800' : 'bg-gray-300 text-gray-500 cursor-not-allowed']">
                    Next
                </button>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { adminService } from '../../services/admin'
import { useScrollToTop } from '../../composables/useScrollToTop'

const { scrollToTop, watchAndScroll } = useScrollToTop()
import AdminPageDescription from './AdminPageDescription.vue'
import Loader from '../Loader.vue'
import { useLoadingState } from '../../composables/useLoadingState'


const careers = ref([])
const showForm = ref(false)

// Watch showForm and scroll to top when it becomes true
watchAndScroll(showForm)

const editingCareer = ref(null)

const formData = ref({
    title: '',
    description: '',
    department: '',
    location: '',
    employment_type: 'full-time',
    subsections: []
})

const addSubsection = () => {
    formData.value.subsections.push({ title: '', content: '' })
}

const removeSubsection = (index) => {
    formData.value.subsections.splice(index, 1)
}

const currentPage = ref(1)
const limit = ref(10)
const pagination = ref(null)
const { loading, setLoading } = useLoadingState(false)

const loadCareers = async (page = 1) => {
    setLoading(true)
    try {
        const response = await adminService.getCareers(page, limit.value)
        if (response.data && response.pagination) {
            careers.value = response.data
            pagination.value = response.pagination
            currentPage.value = page
        } else {
            careers.value = Array.isArray(response) ? response : []
            pagination.value = null
        }
    } catch (error) {
        console.error('Failed to load careers:', error)
        careers.value = []
        pagination.value = null
    } finally {
        setLoading(false)
    }
}

const editCareer = async (career) => {
    try {
        const fullCareer = await adminService.getCareer(career.id)
        editingCareer.value = fullCareer
        formData.value = {
            title: fullCareer.title,
            description: fullCareer.description,
            department: fullCareer.department,
            location: fullCareer.location || '',
            employment_type: fullCareer.employment_type,
            subsections: Array.isArray(fullCareer.subsections) ? fullCareer.subsections : []
        }
        showForm.value = true
    } catch (error) {
        console.error('Failed to load career:', error)
        alert('Failed to load career details')
    }
}

const cancelForm = () => {
    showForm.value = false
    editingCareer.value = null
    formData.value = {
        title: '',
        description: '',
        department: '',
        location: '',
        employment_type: 'full-time',
        subsections: []
    }
}

const saveCareer = async () => {
    try {
        if (editingCareer.value) {
            await adminService.updateCareer(editingCareer.value.id, formData.value)
        } else {
            await adminService.createCareer(formData.value)
        }
        await loadCareers(currentPage.value)
        cancelForm()
        scrollToTop()
    } catch (error) {
        console.error('Failed to save career:', error)
        alert('Failed to save career. Please try again.')
    }
}

const toggleStatus = async (career) => {
    try {
        await adminService.toggleCareerStatus(career.id)
        await loadCareers(currentPage.value)
    } catch (error) {
        console.error('Failed to toggle career status:', error)
        alert('Failed to toggle career status. Please try again.')
    }
}

const deleteCareer = async (id) => {
    if (!confirm('Are you sure you want to delete this career?')) return

    try {
        await adminService.deleteCareer(id)
        await loadCareers(currentPage.value)
    } catch (error) {
        console.error('Failed to delete career:', error)
        alert('Failed to delete career. Please try again.')
    }
}

onMounted(() => {
    loadCareers(1)
})
</script>
